"use client"

export const dynamic = "force-dynamic"

import { useState, useEffect, useCallback } from "react"
import Link from "next/link"
import { Search, Filter, ExternalLink, Calendar, MapPin, Building2, Tag, ChevronLeft, ChevronRight } from "lucide-react"

interface Opportunity {
  id: string
  noticeId: string
  title: string
  noticeType: string | null
  status: string | null
  postedDate: string | null
  responseDeadline: string | null
  naicsCode: string | null
  setAsideDescription: string | null
  agencyName: string | null
  departmentName: string | null
  popCity: string | null
  popState: string | null
  sourceUrl: string | null
  lastSyncedAt: string
}

interface ApiResponse {
  opportunities: Opportunity[]
  total: number
  page: number
  totalPages: number
}

const NOTICE_TYPES: Record<string, string> = {
  o: "Solicitation",
  p: "Pre-Solicitation",
  k: "Combined Synopsis",
  r: "Sources Sought",
  g: "Sale of Surplus Property",
  s: "Special Notice",
  i: "Intent to Bundle",
  a: "Award Notice",
  j: "Justification",
}

function fmtDate(iso: string | null): string {
  if (!iso) return "—"
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
}

function deadlineColor(iso: string | null): string {
  if (!iso) return "text-gray-400"
  const days = Math.floor((new Date(iso).getTime() - Date.now()) / 86400000)
  if (days < 0) return "text-gray-400"
  if (days <= 7) return "text-red-600 font-semibold"
  if (days <= 14) return "text-amber-600"
  return "text-gray-700"
}

export default function OpportunitiesPage() {
  const [data, setData] = useState<ApiResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showFilters, setShowFilters] = useState(false)

  // Filter state
  const [q, setQ] = useState("")
  const [status, setStatus] = useState("")
  const [state, setState] = useState("")
  const [naics, setNaics] = useState("")
  const [noticeType, setNoticeType] = useState("")
  const [page, setPage] = useState(1)

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams({ page: String(page), limit: "20" })
      if (q) params.set("q", q)
      if (status) params.set("status", status)
      if (state) params.set("state", state)
      if (naics) params.set("naics", naics)
      if (noticeType) params.set("noticeType", noticeType)

      const res = await fetch(`/api/gov/opportunities?${params}`)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      setData(await res.json())
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load opportunities")
    } finally {
      setLoading(false)
    }
  }, [q, status, state, naics, noticeType, page])

  useEffect(() => { fetchData() }, [fetchData])

  function applyFilters(e: React.FormEvent) {
    e.preventDefault()
    setPage(1)
    fetchData()
  }

  function clearFilters() {
    setQ(""); setStatus(""); setState(""); setNaics(""); setNoticeType(""); setPage(1)
  }

  const hasFilters = q || status || state || naics || noticeType

  return (
    <div>
      {/* Page header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Open Opportunities</h1>
        <p className="text-gray-500 text-sm mt-1">
          Active federal solicitations from SAM.gov. Click any opportunity to view details and the official source link.
        </p>
      </div>

      {/* Search + filter bar */}
      <form onSubmit={applyFilters} className="bg-white border border-gray-200 rounded-xl p-4 mb-6">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search titles, agencies, descriptions…"
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
          </div>
          <button
            type="button"
            onClick={() => setShowFilters((v) => !v)}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm border rounded-lg transition-colors ${showFilters ? "bg-amber-50 border-amber-400 text-amber-700" : "border-gray-200 text-gray-600 hover:border-gray-300"}`}
          >
            <Filter className="w-4 h-4" />
            Filters
            {hasFilters && <span className="bg-amber-500 text-white rounded-full w-4 h-4 text-[10px] flex items-center justify-center">!</span>}
          </button>
          <button type="submit" className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium rounded-lg transition-colors">
            Search
          </button>
        </div>

        {showFilters && (
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)} className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-amber-500">
                <option value="">All</option>
                <option value="Active">Active</option>
                <option value="Archived">Archived</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">State (pop)</label>
              <input type="text" value={state} onChange={(e) => setState(e.target.value.toUpperCase())} maxLength={2} placeholder="CA" className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-amber-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">NAICS code</label>
              <input type="text" value={naics} onChange={(e) => setNaics(e.target.value)} placeholder="238210" className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-amber-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Notice type</label>
              <select value={noticeType} onChange={(e) => setNoticeType(e.target.value)} className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-amber-500">
                <option value="">All</option>
                {Object.entries(NOTICE_TYPES).map(([code, label]) => (
                  <option key={code} value={code}>{label}</option>
                ))}
              </select>
            </div>
            {hasFilters && (
              <button type="button" onClick={clearFilters} className="text-xs text-gray-500 underline col-span-2 sm:col-span-4 text-left">
                Clear all filters
              </button>
            )}
          </div>
        )}
      </form>

      {/* Results */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700 mb-6">{error}</div>
      )}

      {loading && !data && (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500" />
        </div>
      )}

      {data && (
        <>
          <p className="text-sm text-gray-500 mb-4">
            {data.total.toLocaleString()} {data.total === 1 ? "opportunity" : "opportunities"}
            {hasFilters ? " matching your filters" : " imported"}
          </p>

          {data.opportunities.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
              <p className="text-gray-500 text-sm">No opportunities found.</p>
              {!hasFilters && (
                <p className="text-gray-400 text-xs mt-2">Run an import from the admin dashboard to populate this section.</p>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {data.opportunities.map((opp) => (
                <Link
                  key={opp.id}
                  href={`/federal/opportunities/${opp.id}`}
                  className="block bg-white border border-gray-200 rounded-xl p-4 hover:border-amber-300 hover:shadow-sm transition-all"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        {opp.status && (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${opp.status === "Active" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>
                            {opp.status}
                          </span>
                        )}
                        {opp.noticeType && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-50 text-blue-700">
                            {NOTICE_TYPES[opp.noticeType] ?? opp.noticeType}
                          </span>
                        )}
                        {opp.naicsCode && (
                          <span className="inline-flex items-center gap-1 text-[10px] text-gray-500">
                            <Tag className="w-3 h-3" /> {opp.naicsCode}
                          </span>
                        )}
                      </div>
                      <h3 className="font-semibold text-gray-900 text-sm leading-snug line-clamp-2">{opp.title}</h3>
                      <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-gray-500">
                        {opp.agencyName && (
                          <span className="flex items-center gap-1">
                            <Building2 className="w-3 h-3" />
                            {opp.agencyName}
                          </span>
                        )}
                        {(opp.popCity || opp.popState) && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {[opp.popCity, opp.popState].filter(Boolean).join(", ")}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          Posted {fmtDate(opp.postedDate)}
                        </span>
                        {opp.setAsideDescription && (
                          <span className="text-purple-600">{opp.setAsideDescription}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex-shrink-0 text-right">
                      <p className="text-[10px] text-gray-400 mb-0.5">Response due</p>
                      <p className={`text-xs ${deadlineColor(opp.responseDeadline)}`}>
                        {fmtDate(opp.responseDeadline)}
                      </p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {/* Pagination */}
          {data.totalPages > 1 && (
            <div className="flex items-center justify-between mt-6">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg disabled:opacity-40 hover:border-gray-300 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" /> Previous
              </button>
              <span className="text-sm text-gray-500">
                Page {data.page} of {data.totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(data.totalPages, p + 1))}
                disabled={page >= data.totalPages}
                className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg disabled:opacity-40 hover:border-gray-300 transition-colors"
              >
                Next <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
